/* NutraCore storefront behaviour: drawers, Ajax cart, mega menu, product page, bundle builder, shop filters. */
(() => {
  if (window.__ncThemeLoaded) return;
  window.__ncThemeLoaded = true;

  const T = window.ncTheme || {};
  const routes = T.routes || {};
  const root = routes.root || '/';
  const TIER2 = Number(T.tier2 ?? 10);
  const TIER3 = Number(T.tier3 ?? 15);
  const rateFor = (units) => (units >= 3 ? TIER3 : units === 2 ? TIER2 : 0);
  const $ = (sel, el = document) => el.querySelector(sel);
  const $$ = (sel, el = document) => Array.from(el.querySelectorAll(sel));

  /* ---------- money ---------- */
  function formatMoney(cents, format) {
    const f = format || T.moneyFormat || '${{amount}}';
    const value = Math.round(Number(cents) || 0);
    const fmt = (n, decimals, thousands, dec) => {
      const fixed = (n / 100).toFixed(decimals);
      const [whole, frac] = fixed.split('.');
      const w = whole.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return frac ? w + dec + frac : w;
    };
    return f.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(value, 0, ',', '.');
        case 'amount_with_comma_separator': return fmt(value, 2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(value, 0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(value, 2, "'", '.');
        default: return fmt(value, 2, ',', '.');
      }
    });
  }
  window.ncFormatMoney = formatMoney;

  /* ---------- scroll lock + drawers ---------- */
  let lastOpener = null;
  const openDrawers = new Set();
  const setLock = () => document.documentElement.toggleAttribute('scroll-lock', openDrawers.size > 0);

  function drawerEl(name) { return document.querySelector(`[data-nc-drawer="${name}"]`); }
  function openDrawer(name, opener) {
    const el = drawerEl(name);
    if (!el) return;
    closeMega();
    openDrawers.forEach((n) => n !== name && closeDrawer(n, true));
    lastOpener = opener || document.activeElement;
    el.classList.add('is-open');
    el.setAttribute('aria-hidden', 'false');
    openDrawers.add(name);
    setLock();
    $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    const focusTarget = $('[data-nc-close], button, a[href]', $('.nc-drawer__panel', el));
    window.setTimeout(() => focusTarget && focusTarget.focus({ preventScroll: true }), 60);
  }
  function closeDrawer(name, silent) {
    const el = drawerEl(name);
    if (!el || !el.classList.contains('is-open')) return;
    el.classList.remove('is-open');
    el.setAttribute('aria-hidden', 'true');
    openDrawers.delete(name);
    setLock();
    $$(`[aria-controls="${el.id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    if (!silent && lastOpener && document.contains(lastOpener)) lastOpener.focus({ preventScroll: true });
  }
  window.ncOpenCart = () => openDrawer('cart');

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      openDrawers.forEach((n) => closeDrawer(n));
      closeMega();
      return;
    }
    if (e.key !== 'Tab' || openDrawers.size === 0) return;
    const name = Array.from(openDrawers).pop();
    const panel = $('.nc-drawer__panel', drawerEl(name));
    if (!panel) return;
    const items = $$('a[href], button:not([disabled]), input, select, [tabindex]:not([tabindex="-1"])', panel).filter((x) => x.offsetParent !== null);
    if (!items.length) return;
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    else if (!panel.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
  });

  /* ---------- cart ---------- */
  const SECTION = 'nc-cart-drawer';
  let cartBusy = false;

  async function cartPost(path, body) {
    const res = await fetch(`${path}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: SECTION, sections_url: window.location.pathname }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
    return data;
  }

  function cartInner() { return $('[data-nc-drawer="cart"] [data-nc-cart-inner]'); }

  function renderCart(html) {
    if (!html) return;
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const next = $('[data-nc-cart-inner]', doc);
    const current = cartInner();
    if (!next || !current) return;
    current.replaceWith(next);
    const count = next.dataset.count || '0';
    $$('[data-nc-cart-count]').forEach((el) => { el.textContent = count; });
    $$('.nc-cart-btn').forEach((b) => b.setAttribute('aria-label', `Open cart, ${count} item${count === '1' ? '' : 's'}`));
    document.dispatchEvent(new CustomEvent('nc:cart-updated', { detail: { count: Number(count) } }));
  }

  function showCartError(message) {
    const box = $('[data-nc-cart-error]', cartInner() || document);
    if (box) { box.textContent = message; box.hidden = false; } else { window.alert(message); }
  }

  async function refreshCart() {
    const res = await fetch(`${root}?sections=${SECTION}`);
    const data = await res.json();
    renderCart(data[SECTION]);
  }

  async function addItems(items, trigger) {
    if (cartBusy || !items.length) return false;
    cartBusy = true;
    if (trigger) trigger.setAttribute('aria-busy', 'true');
    try {
      const data = await cartPost(routes.cartAdd || '/cart/add', { items });
      renderCart(data.sections && data.sections[SECTION]);
      openDrawer('cart', trigger);
      cartBusy = false;
      await syncProtection();
      return true;
    } catch (err) {
      await refreshCart().catch(() => {});
      openDrawer('cart', trigger);
      showCartError(err.message);
      return false;
    } finally {
      cartBusy = false;
      if (trigger) trigger.removeAttribute('aria-busy');
    }
  }
  window.ncAddToCart = addItems;

  async function changeLine(key, quantity) {
    if (cartBusy) return;
    cartBusy = true;
    const inner = cartInner();
    if (inner) inner.setAttribute('aria-busy', 'true');
    try {
      const data = await cartPost(routes.cartChange || '/cart/change', { id: key, quantity: Math.max(0, quantity) });
      renderCart(data.sections && data.sections[SECTION]);
      cartBusy = false;
      await syncProtection();
    } catch (err) {
      showCartError(err.message);
    } finally {
      cartBusy = false;
      const i = cartInner();
      if (i) i.removeAttribute('aria-busy');
    }
  }

  /* shipping protection: optional product set in Theme settings → NutraCore */
  const OPT_OUT = 'nc_protection_opt_out';
  const storage = {
    get(k) { try { return window.localStorage.getItem(k); } catch (e) { return null; } },
    set(k, v) { try { window.localStorage.setItem(k, v); } catch (e) { /* storage unavailable */ } },
    del(k) { try { window.localStorage.removeItem(k); } catch (e) { /* storage unavailable */ } },
  };
  async function syncProtection() {
    const inner = cartInner();
    if (!inner || cartBusy) return;
    const count = Number(inner.dataset.count || 0);
    const key = inner.dataset.protectKey;
    const variant = inner.dataset.protectVariant;
    if (!variant) return;
    if (count === 0 && key) {
      await changeLine(key, 0);
    } else if (count > 0 && !key && T.protectionPreselect && storage.get(OPT_OUT) !== '1') {
      cartBusy = true;
      try {
        const data = await cartPost(routes.cartAdd || '/cart/add', { items: [{ id: Number(variant), quantity: 1 }] });
        renderCart(data.sections && data.sections[SECTION]);
      } catch (e) { /* leave the cart as it is */ } finally { cartBusy = false; }
    }
  }

  document.addEventListener('click', async (e) => {
    const t = e.target;
    const opener = t.closest('[data-nc-open]');
    if (opener) { e.preventDefault(); openDrawer(opener.dataset.ncOpen, opener); return; }
    const closer = t.closest('[data-nc-close]');
    if (closer) {
      const d = closer.closest('[data-nc-drawer]');
      if (d) { e.preventDefault(); closeDrawer(d.dataset.ncDrawer); }
      return;
    }
    const add = t.closest('[data-nc-add]');
    if (add) {
      e.preventDefault();
      addItems([{ id: Number(add.dataset.ncAdd), quantity: Number(add.dataset.qty || 1) }], add);
      return;
    }
    const qty = t.closest('[data-nc-qty]');
    if (qty) { e.preventDefault(); changeLine(qty.dataset.key, Number(qty.dataset.ncQty)); return; }
    const protect = t.closest('[data-nc-protect]');
    if (protect) {
      e.preventDefault();
      const inner = cartInner();
      if (!inner || cartBusy) return;
      if (inner.dataset.protectKey) { storage.set(OPT_OUT, '1'); changeLine(inner.dataset.protectKey, 0); }
      else {
        storage.del(OPT_OUT);
        cartBusy = true;
        try {
          const data = await cartPost(routes.cartAdd || '/cart/add', { items: [{ id: Number(inner.dataset.protectVariant), quantity: 1 }] });
          renderCart(data.sections && data.sections[SECTION]);
        } catch (err) { showCartError(err.message); } finally { cartBusy = false; }
      }
      return;
    }
    const scrollBuy = t.closest('[data-nc-scroll-buy]');
    if (scrollBuy) {
      const buy = document.getElementById('NcBuy');
      const target = buy && ($('[data-nc-option]', buy) || $('.nc-packs', buy) || $('[data-nc-form]', buy) || buy);
      if (target) { e.preventDefault(); target.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
    }
  });

  /* ---------- header: mega menu ---------- */
  const header = $('[data-nc-header]');
  const mega = $('#NcMega');
  const megaToggle = $('[data-nc-mega-toggle]');
  let hoverOpenedAt = 0;
  function openMega() {
    if (!mega || !megaToggle) return;
    mega.hidden = false;
    megaToggle.setAttribute('aria-expanded', 'true');
  }
  function closeMega() {
    if (!mega || !megaToggle || mega.hidden) return;
    mega.hidden = true;
    megaToggle.setAttribute('aria-expanded', 'false');
  }
  function activateMegaRow(key) {
    if (!mega) return;
    $$('[data-nc-mega-row]', mega).forEach((r) => r.classList.toggle('is-active', r.dataset.ncMegaRow === key));
    $$('[data-nc-mega-panel]', mega).forEach((p) => { p.hidden = p.dataset.ncMegaPanel !== key; });
  }
  if (header && mega && megaToggle) {
    const hoverMode = header.dataset.megaMode !== 'click';
    megaToggle.addEventListener('click', () => {
      if (hoverMode && !mega.hidden && Date.now() - hoverOpenedAt < 450) return;
      if (mega.hidden) openMega(); else closeMega();
    });
    if (hoverMode) {
      megaToggle.addEventListener('mouseenter', () => { if (mega.hidden) { hoverOpenedAt = Date.now(); openMega(); } });
      header.addEventListener('mouseleave', closeMega);
      $$('[data-nc-mega-close]', header).forEach((a) => a.addEventListener('mouseenter', closeMega));
    }
    $$('[data-nc-mega-row]', mega).forEach((row) => {
      row.addEventListener('mouseenter', () => activateMegaRow(row.dataset.ncMegaRow));
      row.addEventListener('focus', () => activateMegaRow(row.dataset.ncMegaRow));
    });
    document.addEventListener('mousedown', (e) => { if (!mega.hidden && !header.contains(e.target)) closeMega(); });
    window.matchMedia('(max-width: 1023px)').addEventListener('change', (mq) => {
      if (mq.matches) closeMega(); else closeDrawer('nav', true);
    });
  }

  /* ---------- delivery estimate ---------- */
  function addBusinessDays(n) {
    const d = new Date();
    let k = 0;
    while (k < n) { d.setDate(d.getDate() + 1); const w = d.getDay(); if (w !== 0 && w !== 6) k += 1; }
    return d;
  }
  const fmtDate = (d) => d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  $$('[data-nc-delivery]').forEach((el) => {
    const min = Number(el.dataset.min || 5);
    const max = Number(el.dataset.max || 7);
    el.textContent = `${fmtDate(addBusinessDays(min))} – ${fmtDate(addBusinessDays(max))}`;
  });

  /* ---------- UGC videos ---------- */
  document.addEventListener('click', (e) => {
    const play = e.target.closest('[data-nc-ugc-play]');
    if (!play) return;
    const tile = play.closest('.nc-ugc__tile');
    const video = tile && $('video', tile);
    if (!video) return;
    if (video.paused) { video.play(); tile.classList.add('is-playing'); } else { video.pause(); tile.classList.remove('is-playing'); }
  });

  /* ---------- product page ---------- */
  $$('[data-nc-product]').forEach((rootEl) => {
    const dataEl = $('script[data-nc-product-json]', rootEl);
    if (!dataEl) return;
    const P = JSON.parse(dataEl.textContent);
    const form = $('[data-nc-form]', rootEl);
    const idInput = $('input[name="id"]', form);
    const qtyInput = $('input[name="quantity"]', form);
    const atc = $('[data-nc-atc]', rootEl);
    const errorBox = $('[data-nc-error]', rootEl);
    const sticky = $('[data-nc-sticky]', rootEl);
    let variant = P.variants.find((v) => v.id === Number(idInput.value)) || P.variants[0];
    let pack = Number(($('[data-nc-pack][aria-checked="true"]', rootEl) || {}).dataset?.ncPack || 1);

    /* gallery: native swipe track + horizontal thumbnail row */
    const track = $('[data-nc-track]', rootEl);
    const slides = track ? Array.from(track.children) : [];
    const thumbs = $$('[data-nc-thumb]', rootEl);
    const thumbRow = $('[data-nc-thumb-row]', rootEl);
    const pos = $('[data-nc-gallery-pos]', rootEl);
    let gi = 0;
    function markActive(i) {
      gi = i;
      thumbs.forEach((b, k) => { b.classList.toggle('is-active', k === i); b.setAttribute('aria-current', k === i ? 'true' : 'false'); });
      if (pos) pos.textContent = `${i + 1} / ${slides.length}`;
      const t = thumbs[i];
      if (t && thumbRow) thumbRow.scrollTo({ left: t.offsetLeft - (thumbRow.clientWidth - t.clientWidth) / 2, behavior: 'smooth' });
    }
    function showImage(i, instant) {
      if (!track || slides.length < 2) return;
      const n = ((i % slides.length) + slides.length) % slides.length;
      track.scrollTo({ left: n * track.clientWidth, behavior: instant ? 'instant' : 'smooth' });
      markActive(n);
    }
    thumbs.forEach((b, k) => b.addEventListener('click', () => showImage(k)));
    const prev = $('[data-nc-prev]', rootEl);
    const next = $('[data-nc-next]', rootEl);
    if (prev) prev.addEventListener('click', () => showImage(gi - 1));
    if (next) next.addEventListener('click', () => showImage(gi + 1));
    if (track && slides.length > 1) {
      track.addEventListener('scroll', () => {
        const w = Math.max(1, track.clientWidth);
        const i = Math.round(track.scrollLeft / w);
        if (i !== gi && Math.abs(track.scrollLeft - i * w) < 4) markActive(i);
      }, { passive: true });
      track.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowRight') { e.preventDefault(); showImage(gi + 1); }
        if (e.key === 'ArrowLeft') { e.preventDefault(); showImage(gi - 1); }
      });
      /* mouse drag on desktop (touch and trackpads swipe natively) */
      let drag = null;
      track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        drag = { x: e.clientX, left: track.scrollLeft, moved: false };
        track.style.scrollSnapType = 'none';
        track.style.scrollBehavior = 'auto';
      });
      window.addEventListener('pointermove', (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        if (Math.abs(dx) > 3) drag.moved = true;
        track.scrollLeft = drag.left - dx;
      });
      window.addEventListener('pointerup', (e) => {
        if (!drag) return;
        const dx = e.clientX - drag.x;
        const from = Math.round(drag.left / Math.max(1, track.clientWidth));
        drag = null;
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
        const to = Math.abs(dx) > 40 ? from + (dx < 0 ? 1 : -1) : from;
        showImage(Math.max(0, Math.min(slides.length - 1, to)));
      });
      const start = Number(track.dataset.start || 0);
      if (start) showImage(start, true);
      window.addEventListener('resize', () => track.scrollTo({ left: gi * track.clientWidth, behavior: 'instant' }));
    }

    /* variant options */
    const optionGroups = $$('[data-nc-option]', rootEl);
    function selectedOptions() {
      return optionGroups.map((g) => ($('[aria-pressed="true"]', g) || {}).dataset?.value);
    }
    optionGroups.forEach((g, gIndex) => {
      $$('[data-value]', g).forEach((btn) => btn.addEventListener('click', () => {
        $$('[data-value]', g).forEach((b) => b.setAttribute('aria-pressed', b === btn ? 'true' : 'false'));
        const label = $('[data-nc-option-selected]', g);
        if (label) label.textContent = btn.dataset.value;
        const opts = selectedOptions();
        const match = P.variants.find((v) => v.options.every((o, k) => o === opts[k]));
        if (match) setVariant(match, true);
        void gIndex;
      }));
    });

    function packMath(n) {
      const r = rateFor(n);
      const price = Math.round((variant.price * n * (100 - r)) / 100);
      const compare = (variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price) * n;
      return { r, price, compare };
    }
    function updatePacks() {
      $$('[data-nc-pack]', rootEl).forEach((btn) => {
        const n = Number(btn.dataset.ncPack);
        const m = packMath(n);
        const unit = n === 1 ? P.unit : P.units;
        $('[data-nc-pack-title]', btn).textContent = `${n} ${unit}`;
        $('[data-nc-pack-sub]', btn).textContent = n === 1
          ? `${P.hasVariants ? variant.title : P.supply || '1 ' + P.unit}${P.freeShip ? ' · free shipping' : ''}`
          : `${formatMoney(m.price / n)} / ${P.unit} · save ${m.r}%`;
        $('[data-nc-pack-price]', btn).textContent = formatMoney(m.price);
        $('[data-nc-pack-compare]', btn).textContent = m.compare - m.price > 0 ? formatMoney(m.compare) : '';
      });
    }
    function updateBuy() {
      const m = packMath(pack);
      qtyInput.value = pack;
      $$('[data-nc-atc-price]', rootEl).forEach((el) => { el.textContent = formatMoney(m.price); });
      const unit = pack === 1 ? P.unit : P.units;
      const label = `${pack} ${unit}${P.hasVariants ? ' · ' + variant.title : ''}${P.freeShip ? ' · Free shipping' : ''}`;
      $$('[data-nc-sticky-label]', rootEl).forEach((el) => { el.textContent = label; });
      const soldOut = !variant.available;
      [atc, $('[data-nc-sticky-btn]', rootEl)].forEach((b) => { if (b) b.disabled = soldOut; });
      $$('[data-nc-atc-label]', rootEl).forEach((el) => { el.textContent = soldOut ? 'Sold out' : el.dataset.default; });
    }
    function setVariant(v, fromUser) {
      variant = v;
      idInput.value = v.id;
      const hasCompare = v.compare_at_price > v.price;
      const pct = hasCompare ? Math.round((1 - v.price / v.compare_at_price) * 100) : 0;
      $$('[data-nc-price]', rootEl).forEach((el) => { el.textContent = formatMoney(v.price); });
      $$('[data-nc-compare]', rootEl).forEach((el) => { el.textContent = hasCompare ? formatMoney(v.compare_at_price) : ''; el.hidden = !hasCompare; });
      $$('[data-nc-save]', rootEl).forEach((el) => { el.textContent = `Save ${pct}%`; el.hidden = !hasCompare; });
      updatePacks();
      updateBuy();
      if (fromUser) {
        if (v.media_index !== null && v.media_index !== undefined) showImage(v.media_index);
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      }
      document.dispatchEvent(new CustomEvent('nc:variant', { detail: { productId: P.id, variant: v } }));
    }
    $$('[data-nc-pack]', rootEl).forEach((btn) => btn.addEventListener('click', () => {
      pack = Number(btn.dataset.ncPack);
      $$('[data-nc-pack]', rootEl).forEach((b) => b.setAttribute('aria-checked', b === btn ? 'true' : 'false'));
      updateBuy();
    }));
    setVariant(variant, false);

    /* add to cart */
    async function submit(trigger) {
      if (errorBox) errorBox.hidden = true;
      if (!variant.available) return;
      await addItems([{ id: variant.id, quantity: pack }], trigger);
    }
    if (form) form.addEventListener('submit', (e) => { e.preventDefault(); submit(atc); });
    const stickyBtn = $('[data-nc-sticky-btn]', rootEl);
    if (stickyBtn) stickyBtn.addEventListener('click', () => submit(stickyBtn));

    /* sticky bar: shown only once the main button has scrolled out of view above */
    if (sticky && atc && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        sticky.classList.toggle('is-visible', show);
        sticky.setAttribute('aria-hidden', show ? 'false' : 'true');
        document.documentElement.classList.toggle('nc-sticky-pad', show);
        document.documentElement.style.setProperty('--nc-sticky-pad', show ? `${sticky.offsetHeight}px` : '0px');
      });
      io.observe(atc);
    }
  });

  /* ---------- frequently bought together ---------- */
  $$('[data-nc-fbt]').forEach((box) => {
    const items = $$('[data-nc-fbt-item]', box);
    const cta = $('[data-nc-fbt-cta]', box);
    function render() {
      const on = items.filter((i) => i.getAttribute('aria-checked') === 'true');
      const r = rateFor(on.length);
      const sum = on.reduce((a, i) => a + Number(i.dataset.price), 0);
      const comp = on.reduce((a, i) => a + Math.max(Number(i.dataset.compare || 0), Number(i.dataset.price)), 0);
      const total = Math.round((sum * (100 - r)) / 100);
      $('[data-nc-fbt-total]', box).textContent = formatMoney(total);
      const s = $('[data-nc-fbt-compare]', box);
      s.textContent = formatMoney(comp);
      s.hidden = comp - total <= 0;
      $('[data-nc-fbt-save]', box).textContent = r ? `You save ${r}% + free shipping` : (box.dataset.freeLabel || 'Free US shipping');
      $('[data-nc-fbt-cta-label]', box).textContent = on.length > 1 ? `Add ${on.length} items to cart — ${formatMoney(total)}` : `Add to cart — ${formatMoney(total)}`;
    }
    items.forEach((i) => {
      if (i.dataset.main) return;
      i.addEventListener('click', () => { i.setAttribute('aria-checked', i.getAttribute('aria-checked') === 'true' ? 'false' : 'true'); render(); });
    });
    document.addEventListener('nc:variant', (e) => {
      const mainItem = items.find((i) => i.dataset.main);
      if (!mainItem || Number(mainItem.dataset.productId) !== e.detail.productId) return;
      const v = e.detail.variant;
      mainItem.dataset.variant = v.id;
      mainItem.dataset.price = v.price;
      mainItem.dataset.compare = v.compare_at_price || 0;
      const p = $('[data-nc-fbt-price]', mainItem);
      if (p) p.textContent = formatMoney(v.price);
      const tag = $('[data-nc-fbt-tag]', mainItem);
      if (tag && tag.dataset.hasVariants) tag.textContent = `This item · ${v.title}`;
      render();
    });
    if (cta) cta.addEventListener('click', () => {
      const on = items.filter((i) => i.getAttribute('aria-checked') === 'true');
      addItems(on.map((i) => ({ id: Number(i.dataset.variant), quantity: 1 })), cta);
    });
    render();
  });

  /* ---------- shop / collection ---------- */
  $$('[data-nc-collection]').forEach((rootEl) => {
    const grid = $('[data-nc-grid]', rootEl);
    if (!grid) return;
    const cards = $$('[data-nc-card]', grid);
    const banner = $('[data-nc-coll-banner]', grid);
    const empty = $('[data-nc-coll-empty]', grid);
    const sortSel = $('[data-nc-sort]', rootEl);
    const chips = $$('[data-nc-chip]', rootEl);
    const title = $('[data-nc-coll-title]', rootEl);
    const count = $('[data-nc-coll-count]', rootEl);
    const img = $('[data-nc-coll-img]', rootEl);
    const params = new URLSearchParams(window.location.search);
    let group = rootEl.dataset.clientFilter === 'true' ? (params.get('filter') || 'all') : 'all';
    let sort = params.get('sort') || 'best';
    if (sortSel) sortSel.value = sort;

    function apply(updateUrl) {
      const visible = cards.filter((c) => group === 'all' || (c.dataset.groups || '').split(',').includes(group));
      const sorted = visible.slice().sort((a, b) => {
        if (sort === 'low') return Number(a.dataset.price) - Number(b.dataset.price);
        if (sort === 'high') return Number(b.dataset.price) - Number(a.dataset.price);
        return Number(a.dataset.rank) - Number(b.dataset.rank);
      });
      cards.forEach((c) => { c.hidden = !visible.includes(c); });
      const cols = Math.max(1, getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length);
      const after = Math.min(sorted.length, cols * Math.max(1, Math.round(4 / cols)));
      sorted.forEach((c, i) => {
        grid.appendChild(c);
        if (banner && i === after - 1) grid.appendChild(banner);
      });
      cards.filter((c) => c.hidden).forEach((c) => grid.appendChild(c));
      if (banner && !sorted.length) grid.appendChild(banner);
      if (empty) { empty.hidden = sorted.length > 0; grid.appendChild(empty); }
      if (count) count.textContent = String(sorted.length);
      const chip = chips.find((c) => c.dataset.group === group) || chips.find((c) => c.dataset.group === 'all');
      if (rootEl.dataset.clientFilter === 'true') {
        chips.forEach((c) => c.classList.toggle('is-active', c === chip));
        chips.forEach((c) => c.setAttribute('aria-pressed', c === chip ? 'true' : 'false'));
        if (chip && title && chip.dataset.title) title.textContent = chip.dataset.title;
        if (chip && img && chip.dataset.image) { img.src = chip.dataset.image; img.removeAttribute('srcset'); }
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        if (group === 'all') url.searchParams.delete('filter'); else url.searchParams.set('filter', group);
        if (sort === 'best') url.searchParams.delete('sort'); else url.searchParams.set('sort', sort);
        window.history.replaceState({}, '', url);
      }
    }
    chips.forEach((c) => {
      if (c.tagName !== 'BUTTON') return;
      c.addEventListener('click', () => { group = c.dataset.group || 'all'; apply(true); });
    });
    if (sortSel) sortSel.addEventListener('change', () => { sort = sortSel.value; apply(true); });
    let lastCols = 0;
    window.addEventListener('resize', () => {
      const c = getComputedStyle(grid).gridTemplateColumns.split(' ').filter(Boolean).length;
      if (c !== lastCols) { lastCols = c; apply(false); }
    });
    apply(false);
  });

  /* ---------- bundle builder ---------- */
  $$('[data-nc-bundle]').forEach((rootEl) => {
    const cards = $$('[data-nc-bb-card]', rootEl);
    const bar = $('[data-nc-bb-bar]', rootEl);
    const state = new Map();
    const cardVariant = (card) => {
      const sel = $('[data-nc-bb-variant]', card);
      const o = sel ? sel.selectedOptions[0] : null;
      return o
        ? { id: Number(o.value), price: Number(o.dataset.price), compare: Number(o.dataset.compare || 0), img: card.dataset.img, title: o.textContent }
        : { id: Number(card.dataset.variant), price: Number(card.dataset.price), compare: Number(card.dataset.compare || 0), img: card.dataset.img };
    };
    function render() {
      let units = 0;
      let sum = 0;
      const slots = [];
      cards.forEach((card, k) => {
        const entry = state.get(k);
        const q = entry ? entry.qty : 0;
        card.classList.toggle('is-in', q > 0);
        $('[data-nc-bb-in]', card).hidden = q === 0;
        $('[data-nc-bb-in]', card).textContent = `In bundle ×${q}`;
        $('[data-nc-bb-add]', card).hidden = q > 0;
        $('[data-nc-bb-step]', card).hidden = q === 0;
        $('[data-nc-bb-qty]', card).textContent = String(q);
        if (q) {
          units += q;
          sum += entry.v.price * q;
          for (let i = 0; i < q; i += 1) slots.push(entry.v.img);
        }
      });
      const r = rateFor(units);
      const total = Math.round((sum * (100 - r)) / 100);
      const slotWrap = $('[data-nc-bb-slots]', bar);
      const shown = slots.slice(0, 5);
      while (shown.length < 3) shown.push('');
      slotWrap.innerHTML = '';
      shown.forEach((src) => {
        const s = document.createElement('span');
        s.className = 'nc-bb-slot';
        if (src) { const im = document.createElement('img'); im.src = src; im.alt = ''; s.appendChild(im); } else { s.textContent = '+'; }
        slotWrap.appendChild(s);
      });
      const msg = units === 0 ? 'Pick your first formula'
        : units === 1 ? `1 item — add 1 more to save ${TIER2}%`
        : units === 2 ? `2 items — saving ${TIER2}%. Add 1 more for ${TIER3}%`
        : `${units} items — best price, saving ${TIER3}%`;
      $('[data-nc-bb-msg]', bar).textContent = msg;
      $$('[data-nc-bb-seg]', bar).forEach((s, i) => s.classList.toggle('on', units >= i + 1));
      $('[data-nc-bb-total]', bar).textContent = formatMoney(total);
      const cmp = $('[data-nc-bb-compare]', bar);
      cmp.textContent = formatMoney(sum);
      cmp.hidden = r === 0;
      $('[data-nc-bb-btn]', bar).disabled = units === 0;
      $$('[data-nc-bb-tier]', rootEl).forEach((t) => {
        const n = Number(t.dataset.ncBbTier);
        t.classList.toggle('is-on', n === 3 ? units >= 3 : units === n);
      });
    }
    cards.forEach((card, k) => {
      const setQty = (q) => {
        if (q <= 0) state.delete(k); else state.set(k, { qty: q, v: cardVariant(card) });
        render();
      };
      $('[data-nc-bb-add]', card).addEventListener('click', () => setQty(1));
      $('[data-nc-bb-inc]', card).addEventListener('click', () => setQty((state.get(k)?.qty || 0) + 1));
      $('[data-nc-bb-dec]', card).addEventListener('click', () => setQty((state.get(k)?.qty || 0) - 1));
      const sel = $('[data-nc-bb-variant]', card);
      if (sel) sel.addEventListener('change', () => {
        const o = sel.selectedOptions[0];
        const priceEl = $('[data-nc-bb-price]', card);
        const cmpEl = $('[data-nc-bb-cmp]', card);
        if (priceEl) priceEl.textContent = formatMoney(o.dataset.price);
        if (cmpEl) { const c = Number(o.dataset.compare || 0); cmpEl.textContent = c > Number(o.dataset.price) ? formatMoney(c) : ''; }
        if (state.has(k)) state.set(k, { qty: state.get(k).qty, v: cardVariant(card) });
        render();
      });
    });
    const btn = $('[data-nc-bb-btn]', bar);
    btn.addEventListener('click', async () => {
      const items = [];
      state.forEach((entry) => items.push({ id: entry.v.id, quantity: entry.qty }));
      const ok = await addItems(items, btn);
      if (ok) { state.clear(); render(); }
    });
    render();
  });

  /* newsletter: keep the success message in view after Shopify's redirect */
  if (window.location.search.includes('customer_posted=true') || window.location.hash === '#nc-newsletter') {
    const n = document.getElementById('nc-newsletter');
    if (n) n.scrollIntoView({ block: 'center' });
  }
})();
